import * as Yup from 'yup';
import logger from '../utils/logger';
import { _SubscriptionPlan, _AddSubscriptionPlanFormType, _HttpMethods } from '../constants/staticTypes';
import { QueryClient } from 'react-query';
import HttpServiceHelper from './httpServiceHelper';
import {
    getSubscriptionPlansApi,
    getFeatureListForPlansApi,
    updateSubscriptionPlanApi,
    getAddonPlansApi,
    createAddonPlanApi,
    updateAddonPlanApi,
    getAddonByIdApi,
    deleteAddonPlanApi,
    archiveAddonApi,
} from '../constants/apiEndPoints';

export const initialAddSubscriptionPlanValues: _AddSubscriptionPlanFormType = {
    planName: '',
    planIcon: 'ri-gift-line',
    description: '',
    pricing: 0,
    pricingPeriod: 'Monthly',
    features: [],
    isRecommended: false,
};

// Validation schema
export const addSubscriptionPlanValidationSchema = Yup.object().shape({
    planName: Yup.string().required('Plan name is required'),
    planIcon: Yup.string().required('Plan icon is required'),
    description: Yup.string().required('Description is required'),
    pricing: Yup.number().min(0, 'Price must be 0 or greater').required('Price is required'),
    pricingPeriod: Yup.string().required('Plan duration is required'),
    features: Yup.array().min(1, 'At least one feature is required'),
});

// Plan icon options
export const planIconOptions = [
    { label: 'Gift', value: 'ri-gift-line', icon: 'ri-gift-line' },
    { label: 'Star', value: 'ri-star-line', icon: 'ri-star-line' },
    { label: 'Crown', value: 'ri-vip-crown-line', icon: 'ri-vip-crown-line' },
    { label: 'Medal', value: 'ri-medal-line', icon: 'ri-medal-line' },
    { label: 'Diamond', value: 'ri-gemstone-line', icon: 'ri-gemstone-line' },
    { label: 'Rocket', value: 'ri-rocket-line', icon: 'ri-rocket-line' },
];

// Plan duration options
export const planDurationOptions = [
    { label: 'Monthly', value: 'Monthly' },
    { label: 'Yearly', value: 'Yearly' },
];

// Feature options
export const featureOptions = [
    { label: 'F&B Menu', value: 'F&B Menu' },
    { label: 'Scheduling', value: 'Scheduling' },
    { label: 'File Operations', value: 'File Operations' },
    { label: 'Back of the House', value: 'Back of the House' },
    { label: 'Basic Order Management', value: 'Basic Order Management' },
    { label: 'Up to 5 menu items', value: 'Up to 5 menu items' },
    { label: 'Standard support', value: 'Standard support' },
    { label: 'Enhanced reporting', value: 'Enhanced reporting' },
    { label: 'Unlimited menu items', value: 'Unlimited menu items' },
    { label: 'Priority support', value: 'Priority support' },
];

// Helper function to get badge colors based on plan name
export const getPlanBadgeColors = (planName: string) => {
    const colorMap: Record<string, { backgroundColor: string; color: string }> = {
        Bronze: {
            backgroundColor: '#FFF4E6',
            color: '#F57C00',
        },
        Silver: {
            backgroundColor: '#E8F4FD',
            color: '#0288D1',
        },
        Gold: {
            backgroundColor: '#FFF9E6',
            color: '#F9A825',
        },
        Starter: {
            backgroundColor: '#E8E8E8',
            color: '#616161',
        },
        Growth: {
            backgroundColor: '#F0E6FF',
            color: '#7B1FA2',
        },
    };

    return colorMap[planName] || { backgroundColor: '#F0E6FF', color: '#7B1FA2' };
};

// Dummy data for subscription plans
export const subscriptionPlansData: _SubscriptionPlan[] = [
    {
        id: '1',
        planName: 'Bronze',
        subscriberCount: 5,
        pricing: 49.0,
        pricingPeriod: 'Month',
        features: 'Basic order management, Limited menu ma..',
        status: 'Active',
        isActive: true,
    },
    {
        id: '2',
        planName: 'Silver',
        subscriberCount: 15,
        pricing: 99.0,
        pricingPeriod: 'Month',
        features: 'All basic plan features, Enhanced reporting..',
        status: 'Active',
        isActive: true,
    },
    {
        id: '3',
        planName: 'Gold',
        subscriberCount: 10,
        pricing: 199.0,
        pricingPeriod: 'Month',
        features: 'All standard plan features, Unlimited menu..',
        status: 'Active',
        isActive: true,
    },
    {
        id: '4',
        planName: 'Starter',
        subscriberCount: 5,
        pricing: 0,
        pricingPeriod: 'Month',
        features: 'Basic Features',
        status: 'Active',
        isActive: true,
    },
    {
        id: '5',
        planName: 'Growth',
        subscriberCount: 0,
        pricing: 29.0,
        pricingPeriod: 'Month',
        features: 'Basic order management, Limited menu ma..',
        status: 'Inactive',
        isActive: false,
    },
];

// Types for API response
export type _SubscriptionPlanPrice = {
    id: string;
    priceId: string;
    planId: string;
    amount: string;
    currency: string;
    type: string;
    recurring: {
        meter: string | null;
        interval: string;
        usage_type: string;
        interval_count: number;
        trial_period_days: number | null;
    };
    isActive: boolean;
    isAddon: boolean;
    metadata: Record<string, any>;
    createdAt: string;
    updatedAt: string;
    deletedAt: string | null;
    additionalData: Record<string, any>;
    plan_id: string;
};

export type _PlanFeature = {
    id: string;
    planId: string;
    featureId: string;
    limitValue: string | null;
    limitUnit: string;
    isUnlimited: boolean;
    metadata: Record<string, any>;
    featureOptionId: string;
    productFeatureId: string | null;
    createdAt: string;
    updatedAt: string;
    deletedAt: string | null;
    feature_option_id: string;
    feature_id: string;
    plan_id: string;
    feature: {
        id: string;
        featureKey: string;
        name: string;
        description: string;
        isAddon: boolean;
        sourceType: string;
        sourceId: string;
        metadata: Record<string, any>;
        isActive: boolean;
        lookupKey: string;
        createdAt: string;
        updatedAt: string;
        deletedAt: string | null;
    };
    featureOption: {
        id: string;
        featureId: string;
        optionName: string;
        optionDescription: string;
        optionUnitList: string[];
        metadata: Record<string, any>;
        createdAt: string;
        updatedAt: string;
        deletedAt: string | null;
        feature_id: string;
    };
};

export type _SubscriptionPlanFromAPI = {
    id: string;
    productId: string;
    name: string;
    description: string;
    isActive: boolean;
    isAddon: boolean;
    metadata: {
        planIconClass?: string;
        setAsRecommended?: string;
    };
    createdAt: string;
    updatedAt: string;
    deletedAt: string | null;
    additionalData: {
        marketing_features?: Array<{ name: string }>;
        [key: string]: any;
    };
    prices: _SubscriptionPlanPrice[];
    planFeatures: _PlanFeature[];
};

// Types for feature options and features for plans
export type _FeatureOption = {
    id: string;
    featureId: string;
    optionName: string;
    optionDescription: string;
    optionUnitList: string[];
    metadata: Record<string, any>;
    createdAt: string;
    updatedAt: string;
    deletedAt: string | null;
    feature_id: string;
};

export type _AssociatedPlan = {
    id: string;
    name: string;
};

export type _SourceModule = {
    id: string;
    moduleName: string;
    moduleDescription: string;
    isDefault: boolean;
    deletedBy: string | null;
    createdAt: string;
    updatedAt: string;
    deletedAt: string | null;
    isActive?: boolean;
};

export type _FeatureForPlan = {
    id: string;
    featureKey: string;
    name: string;
    description: string;
    isAddon: boolean;
    sourceType: string;
    sourceId: string;
    metadata: Record<string, any>;
    isActive: boolean;
    lookupKey: string;
    createdAt: string;
    updatedAt: string;
    deletedAt: string | null;
    featureOptions: _FeatureOption[];
    sourceModule: _SourceModule;
    associatedPlans: _AssociatedPlan[];
};

// the plans endpoint returns a flat array with no pagination, so screens that need every plan ask for this many
export const ALL_SUBSCRIPTION_PLANS_LIMIT = 500;

// every plan, for the client-paged Plans tab and the Associated Plans dropdown (one shared cache entry)
export const getAllSubscriptionPlans = async (queryClient: QueryClient) => {
    const response = await HttpServiceHelper({
        url: `${getSubscriptionPlansApi}?page=1&limit=${ALL_SUBSCRIPTION_PLANS_LIMIT}`,
        method: _HttpMethods.GET,
        queryClient,
    });
    if (Array.isArray(response?.data?.data) && response.data.data.length >= ALL_SUBSCRIPTION_PLANS_LIMIT) {
        logger.warn('Subscription plans response hit the limit and may be truncated', ALL_SUBSCRIPTION_PLANS_LIMIT);
    }
    return response;
};

// API function to get subscription plans
export const getSubscriptionPlansList = (queryClient: QueryClient, page: number = 1, limit: number = 20) => {
    const url = `${getSubscriptionPlansApi}?page=${page}&limit=${limit}`;

    return HttpServiceHelper({
        url,
        method: _HttpMethods.GET,
        queryClient,
    });
};

// API function to get features list for plans
export const getFeatureListForPlans = (queryClient: QueryClient, page: number = 1, limit: number = 100) => {
    const url = `${getFeatureListForPlansApi}?page=${page}&limit=${limit}`;

    return HttpServiceHelper({
        url,
        method: _HttpMethods.GET,
        queryClient,
    });
};

// API function to update subscription plan
export const updateSubscriptionPlan = (queryClient: QueryClient, data: any) => {
    return HttpServiceHelper({
        url: updateSubscriptionPlanApi,
        method: _HttpMethods.PUT,
        queryClient,
        data,
    });
};

// API function to create subscription plan
export const createSubscriptionPlan = (queryClient: QueryClient, data: any) => {
    return HttpServiceHelper({
        url: '/private/subscription/subscription-plan',
        method: _HttpMethods.POST,
        queryClient,
        data,
    });
};

// API function to delete subscription plan
export const deleteSubscriptionPlan = (queryClient: QueryClient, id: string) => {
    return HttpServiceHelper({
        url: '/private/subscription/subscription-plan',
        method: _HttpMethods.DELETE,
        queryClient,
        data: { id },
    });
};

// Types for Addon Plans API response
export type _AddonPlanFromAPI = {
    id: string;
    productId: string;
    name: string;
    description: string;
    isActive: boolean;
    isAddon: boolean;
    metadata: Record<string, any>;
    createdAt: string;
    updatedAt: string;
    deletedAt: string | null;
    additionalData: {
        url: string | null;
        type: string;
        images: string[];
        object: string;
        livemode: boolean;
        tax_code: string | null;
        shippable: string | null;
        attributes: string[];
        unit_label: string | null;
        default_price: string | null;
        marketing_features: Array<{ name: string }>;
        package_dimensions: string | null;
        statement_descriptor: string | null;
    };
    prices?: _SubscriptionPlanPrice[]; // Optional for editing
    planFeatures?: _PlanFeature[]; // Optional for editing
};

export type _AddonPlansAPIResponse = {
    code: string;
    message: string;
    data: {
        data: _AddonPlanFromAPI[];
        pagination: {
            currentPage: number;
            from: number;
            lastPage: number;
            nextPageUrl: string | null;
            path: string;
            limit: number;
            prevPageUrl: string | null;
            to: number;
            total: number;
        };
    };
    details: any;
};

// API function to get addon plans list
export const getAddonPlansList = (queryClient: QueryClient, page: number = 1, limit: number = 20) => {
    const url = `${getAddonPlansApi}?page=${page}&limit=${limit}`;

    return HttpServiceHelper({
        url,
        method: _HttpMethods.GET,
        queryClient,
    });
};

// Types for Create Addon Request
export type _CreateAddonPrice = {
    amount: number;
    currency: string;
    interval: string;
    type: 'one_time' | 'recurring';
    intervalCount: number;
    metadata?: {
        displayName?: string;
        recommended?: boolean;
        savings?: string;
    };
};

export type _CreateAddonLinkedFeature = {
    featureId: string;
    featureOptionId: string;
    featureOptionUnit: string;
    isUnlimited: boolean;
};

export type _CreateAddonRequest = {
    name: string;
    description: string;
    marketingFeatures: string[];
    prices: _CreateAddonPrice[];
    linkedFeatures: _CreateAddonLinkedFeature[];
};

// Types for Update Addon Request
export type _UpdateAddonLinkedFeature = {
    featureId: string;
    featureOptionId: string;
    featureOptionUnit: string;
    isUnlimited: boolean;
    planFeatureId?: string; // Optional, present for existing features
};

export type _UpdateAddonRequest = {
    id: string;
    name?: string;
    description?: string;
    isActive?: boolean;
    marketingFeatures?: string[];
    activePriceIds?: string[];
    newPrices?: _CreateAddonPrice[];
    linkedFeatures?: _UpdateAddonLinkedFeature[];
    removedPlanFeatureIds?: string[];
};

// Types for Create Addon Response
export type _CreateAddonPriceResponse = {
    local: {
        id: string;
        priceId: string;
        planId: string;
        amount: string;
        currency: string;
        type: string;
        isAddon: boolean;
        isActive: boolean;
        recurring: Record<string, any>;
        additionalData: Record<string, any>;
        updatedAt: string;
        createdAt: string;
        metadata: Record<string, any>;
        deletedAt: string | null;
    };
    stripe: Record<string, any>;
};

export type _CreateAddonLinkedFeatureResponse = {
    id: string;
    planId: string;
    featureId: string;
    featureOptionId: string;
    limitUnit: string;
    isUnlimited: boolean;
    updatedAt: string;
    createdAt: string;
    limitValue: string | null;
    metadata: Record<string, any>;
    deletedAt: string | null;
};

export type _CreateAddonResponse = {
    code: string;
    message: string;
    data: {
        addon: _AddonPlanFromAPI;
        prices: _CreateAddonPriceResponse[];
        linkedFeatures: _CreateAddonLinkedFeatureResponse[];
        stripe: Record<string, any>;
    };
    details: any;
};

// API function to create addon plan
export const createAddonPlan = (queryClient: QueryClient, data: _CreateAddonRequest) => {
    return HttpServiceHelper({
        url: createAddonPlanApi,
        method: _HttpMethods.POST,
        queryClient,
        data,
    });
};

// API function to update addon plan
export const updateAddonPlan = (queryClient: QueryClient, data: _UpdateAddonRequest) => {
    return HttpServiceHelper({
        url: updateAddonPlanApi,
        method: _HttpMethods.PUT,
        queryClient,
        data,
    });
};

// API function to get addon by ID with full details
export const getAddonById = (queryClient: QueryClient, id: string) => {
    const url = `${getAddonByIdApi}/${id}`;

    return HttpServiceHelper({
        url,
        method: _HttpMethods.GET,
        queryClient,
    });
};

// API function to delete addon plan
export const deleteAddonPlan = (queryClient: QueryClient, id: string) => {
    return HttpServiceHelper({
        url: deleteAddonPlanApi,
        method: _HttpMethods.DELETE,
        queryClient,
        data: { id },
    });
};

// API function to archive/unarchive addon plan (change status)
export const archiveAddonPlan = (queryClient: QueryClient, id: string, status: boolean) => {
    const url = `${archiveAddonApi}/${id}/${status}`;

    return HttpServiceHelper({
        url,
        method: _HttpMethods.GET,
        queryClient,
    });
};
